import React, { useState } from 'react';
import {
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    HStack,
    Tabs,
    TabList,
    useDisclosure,
    VStack,
    TabPanel,
    modals,
    Input,
    Button,
    FormControl,
    Tab,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { NewEventButton } from '../../../event-panel/new-event-button';
import {
    Tabs as TabsType,
    useMonitoredEventsDrawerTab,
} from './state/monitored-entities-drawer-context';
import { useOpenEventPanels } from './state/use-open-event-panels';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useOpenBoloPanels } from './state/use-open-bolo-panels';
import { useOpenAttachmentsPanels } from './state/use-open-attachments-panels';
import { ExistingEventItem } from './existing-event-item';
import { EventItem } from './event-item';
import { BoloItem } from './bolo-item';
import { AttachmentsItem } from './attachments-item';

export function MonitoredEntitiesDrawer(
    props: Pick<ReturnType<typeof useDisclosure>, 'isOpen' | 'onClose'>
): React.ReactElement {
    return (
        <Drawer
            variant="inline"
            isOpen={props.isOpen}
            size="md"
            onClose={props.onClose}
            placement="left"
        >
            <MonitoredEventsDrawerContent />
        </Drawer>
    );
}

const MonitoredEventsDrawerContent = observer(
    function MonitoredEventsDrawerContent(): React.ReactElement {
        const { viewBolos, mediaAttachmentsEnabled } = useEnabledFeatures();
        const [searchText, setSearchText] = useState<string>('');
        const tabContext = useMonitoredEventsDrawerTab();
        const {
            filteredEventPanels,
            filteredRecentEventPanels,
            allMonitoredEventPanels,
            allEventWorkspacePanels,
        } = useOpenEventPanels(searchText);
        const {
            filteredBoloPanels,
            filteredRecentBoloPanels,
            allBoloWorkspacePanels,
        } = useOpenBoloPanels(searchText);
        const {
            filteredAttachmentsPanels,
            filteredRecentAttachmentsPanels,
            allAttachmentsWorkspacePanels,
        } = useOpenAttachmentsPanels(searchText);

        return (
            <DrawerContent>
                <DrawerHeader>{`Monitored Events (${allMonitoredEventPanels.length})`}</DrawerHeader>
                <DrawerBody>
                    <VStack alignItems="flex-start">
                        <FormControl
                            helpText={
                                'Search by Event #, CFS' + (viewBolos ? ', BOLO #, Bolo Type' : '')
                            }
                        >
                            <Input
                                leftIcon="Search"
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                }}
                            />
                        </FormControl>
                        <HStack w="100%" justifyContent="space-between">
                            <NewEventButton variant="ghost" size="md" />
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    modals.confirm({
                                        title:
                                            'Close All Events' +
                                            (viewBolos ? ' and BOLOs' : '') +
                                            (mediaAttachmentsEnabled
                                                ? ' with associated Attachments'
                                                : ''),
                                        body: `Click Confirm to close all event${
                                            viewBolos ? ' and BOLOs' : ''
                                        }${
                                            mediaAttachmentsEnabled
                                                ? ' with associated attachments'
                                                : ''
                                        } panels`,
                                        onConfirm() {
                                            allEventWorkspacePanels.forEach((panel) =>
                                                panel.close()
                                            );
                                            allBoloWorkspacePanels.forEach((panel) =>
                                                panel.close()
                                            );
                                            if (mediaAttachmentsEnabled) {
                                                allAttachmentsWorkspacePanels.forEach((panel) =>
                                                    panel.close()
                                                );
                                            }
                                        },
                                    });
                                }}
                            >
                                Close All
                            </Button>
                        </HStack>
                        <Tabs
                            style={{ width: '100%' }}
                            value={tabContext?.tab ?? 'monitored'}
                            onValueChange={(val) => {
                                tabContext?.setTab(val as TabsType);
                            }}
                        >
                            <TabList style={{ width: '100%' }}>
                                <Tab value="monitored">
                                    {`Monitored (${
                                        filteredEventPanels.length +
                                        (viewBolos ? filteredBoloPanels.length : 0) +
                                        (mediaAttachmentsEnabled
                                            ? filteredAttachmentsPanels.length
                                            : 0)
                                    })`}
                                </Tab>
                                <Tab value="recent">{`Recent (${
                                    filteredRecentEventPanels.length +
                                    (viewBolos ? filteredRecentBoloPanels.length : 0) +
                                    (mediaAttachmentsEnabled
                                        ? filteredRecentAttachmentsPanels.length
                                        : 0)
                                })`}</Tab>
                            </TabList>
                            <TabPanel
                                value="monitored"
                                style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}
                            >
                                <VStack w="100%" alignItems="flex-start" spacing={2}>
                                    {filteredEventPanels.map((eventPanel) => (
                                        <ExistingEventItem
                                            eventPanel={eventPanel}
                                            key={eventPanel.workspacePanel.uuid}
                                        />
                                    ))}
                                    {filteredBoloPanels.map(({ workspacePanel, metadata }) => (
                                        <BoloItem
                                            key={workspacePanel.uuid}
                                            workspacePanel={workspacePanel}
                                            title={metadata?.title ?? 'New BOLO'}
                                            boloId={metadata?.boloId}
                                            boloNumber={metadata?.boloNumber}
                                        />
                                    ))}
                                    {mediaAttachmentsEnabled &&
                                        filteredAttachmentsPanels.map(
                                            ({ workspacePanel, metadata }) => (
                                                <AttachmentsItem
                                                    key={workspacePanel.uuid}
                                                    workspacePanel={workspacePanel}
                                                    title={
                                                        // TODO: media attachments add bootstrap for attachments panel. will get entityNumber from bootstrap
                                                        metadata?.entityType === 'BOLO'
                                                            ? `Bolo #${
                                                                  metadata?.entityNumber || ''
                                                              }`
                                                            : `Event #${
                                                                  metadata?.entityNumber || ''
                                                              }`
                                                    }
                                                    entityId={metadata?.entityId}
                                                    entityType={metadata?.entityType ?? ''}
                                                    entityNumber={metadata?.entityNumber}
                                                />
                                            )
                                        )}
                                </VStack>
                            </TabPanel>
                            <TabPanel
                                value="recent"
                                style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}
                            >
                                <VStack w="100%" alignItems="flex-start" spacing={2}>
                                    {filteredRecentEventPanels.map((recentEventData) => (
                                        <EventItem
                                            key={recentEventData.eventId}
                                            isHistorical={true}
                                            eventId={recentEventData.eventId}
                                            agencyEventNumber={recentEventData.agencyEventNumber}
                                            agencyTypeGlobalAttrId={
                                                recentEventData.agencyTypeGlobalAttrId
                                            }
                                            cfsText={[
                                                recentEventData.cfsCode,
                                                recentEventData.cfsDescription,
                                            ]
                                                .filter((x) => !!x)
                                                .join(' - ')}
                                        />
                                    ))}
                                    {filteredRecentBoloPanels.map((recentBoloData) => (
                                        <BoloItem
                                            key={recentBoloData.boloId}
                                            isHistorical
                                            title={recentBoloData.title}
                                            boloId={recentBoloData.boloId}
                                            boloNumber={recentBoloData.boloNumber}
                                        />
                                    ))}
                                    {mediaAttachmentsEnabled &&
                                        filteredRecentAttachmentsPanels.map(
                                            (recentAttachmentsData) => (
                                                <AttachmentsItem
                                                    key={`${recentAttachmentsData.entityId}-${recentAttachmentsData.entityType}`}
                                                    isHistorical
                                                    title={
                                                        recentAttachmentsData.entityType === 'BOLO'
                                                            ? `Bolo #${
                                                                  recentAttachmentsData.entityNumber ||
                                                                  ''
                                                              }`
                                                            : `Event #${
                                                                  recentAttachmentsData.entityNumber ||
                                                                  ''
                                                              }`
                                                    }
                                                    entityId={recentAttachmentsData.entityId}
                                                    entityType={recentAttachmentsData.entityType}
                                                    entityNumber={
                                                        recentAttachmentsData.entityNumber
                                                    }
                                                />
                                            )
                                        )}
                                </VStack>
                            </TabPanel>
                        </Tabs>
                    </VStack>
                </DrawerBody>
            </DrawerContent>
        );
    }
);
